import type { Nil, PartialWithNil } from "@mcwd/typescript-type-guards";
import type { FormWidgetData } from "../../../widgetDataTs.js";
import type { AnyFormSettingsKey } from "../../form-settings/form-experience/form-settings-keys.js";
import { toValue, watch, computed } from "vue";
import type { MaybeRefOrGetter, ComputedRef } from "vue";
import type { HiddenMarketoInputType } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
import { useGatingLevel } from "../../composables/use-gating-level.js";
import type { SiteCountryCodeUppercase, LocaleUrlPrefix } from "../../../types-and-definitions/locale-defs/Locales.js";
import { useFormLogger } from "../../composables/use-form-logger.js";
import type { GatingLevel } from "../../types/definitions/gating/gating-level.js";
import type { GatingState } from "../../types/definitions/gating/gating-state.js";
import { checkObjectValuesEqual } from "./checkObjectValuesEqual.js";
import { getHiddenFieldValues } from "./getHiddenFieldValues.js";

export function getHiddenFieldValuesReactive ({
  formGatingState,
  experienceGatingState,
  widgetData,
  ...nonReactiveOpts
}: {
  formSettingsKey: AnyFormSettingsKey;
  leadSource: string | Nil;
  formTrackingName: string;
  siteCountryCode: SiteCountryCodeUppercase;
  localeUrlPrefix: LocaleUrlPrefix;
  customDocumentDownloadURL?: string | undefined;
  initialFormGatingLevel: GatingLevel.MarketoForm;
  initialFormExperienceGatingLevel: GatingLevel.FormExperience;
  formGatingState: MaybeRefOrGetter<GatingState.MarketoForm>;
  experienceGatingState: MaybeRefOrGetter<GatingState.FormExperience>;
  widgetData: MaybeRefOrGetter<FormWidgetData>;
})
{
  const hiddenFieldValues = computed(() => {
    return getHiddenFieldValues({
      ...nonReactiveOpts,
      formGatingState: toValue(formGatingState),
      experienceGatingState: toValue(experienceGatingState),
      widgetData: toValue(widgetData),
    });
  });
  return hiddenFieldValues;
}

export class HiddenFieldValueBuilder
{
  hiddenFieldValuesObject: ComputedRef<PartialWithNil<HiddenMarketoInputType>>;

  constructor (opts: {
    formSettingsKey: AnyFormSettingsKey;
    widgetData: MaybeRefOrGetter<FormWidgetData>;
    leadSource: string | Nil;
    formTrackingName: string;
    siteCountryCode: SiteCountryCodeUppercase;
    localeUrlPrefix: LocaleUrlPrefix;
    customDocumentDownloadURL?: string | undefined;
  })
  {
    const { logger } = useFormLogger();
    const {
      FormGatingLevel,
      FormExperienceGatingLevel,
      CurrentFormExperienceGatingState,
      CurrentMarketoFormGatingState
    } = useGatingLevel({ callerLabel: "hiddenFieldValueBuilder" });
    this.hiddenFieldValuesObject = getHiddenFieldValuesReactive({
      ...opts,
      experienceGatingState: CurrentFormExperienceGatingState,
      formGatingState: CurrentMarketoFormGatingState,
      initialFormExperienceGatingLevel: FormExperienceGatingLevel,
      initialFormGatingLevel: FormGatingLevel
    });

    watch(() => this.hiddenFieldValuesObject,
      (prevValue, newValue) =>
      {
        if (prevValue == null && (newValue == null || Object.keys(newValue).length === 0)) {
          return;
        }
        if (newValue != null && checkObjectValuesEqual(newValue, prevValue) === false) {
          logger.table(newValue);
        }
      },
      { immediate: true }
    );
  }
}